var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useEffect } from "react";
import { useCustomElementWithProps } from "./useCustomElementWithProps";
// So far we assume that the only way to reload a web component is to reload the whole page
const loadedWebcomponents = new Set();
const assetsGetters = {
    CraWebpack4: getAssetsFromWebpack4AssetManifest,
    Vite: getAssetsFromViteAssetManifest,
};
/**
 * Loads scripts, listed in /asset-manifest.json deployed with the microfrontend.
 * Assigns customProps to the custom DOM element.
 */
export function Microfrontend(_a) {
    var { baseUrl, tagName, bundlerTool = "CraWebpack4", devScriptPath = "src/index.tsx", customProps } = _a, htmlProps = __rest(_a, ["baseUrl", "tagName", "bundlerTool", "devScriptPath", "customProps"]);
    useEffect(() => {
        if (!loadedWebcomponents.has(baseUrl)) {
            loadedWebcomponents.add(baseUrl);
            const getAssets = assetsGetters[bundlerTool];
            getAssets(baseUrl)
                .then((assets) => assets.scriptUrl)
                .catch((error) => {
                if (bundlerTool === "Vite") {
                    console.info("Failed to fetch asset manifest. Assuming we are in DEV mode, running against unbundled sources");
                    ensureViteDevPreamble(baseUrl);
                    const devScriptUrl = new URL(devScriptPath, baseUrl).toString();
                    return devScriptUrl;
                }
                else {
                    return Promise.reject(error);
                }
            })
                .then((scriptUrl) => {
                document.head.appendChild(Object.assign(document.createElement("script"), {
                    src: scriptUrl,
                    type: bundlerTool === "Vite" ? "module" : "",
                }));
            });
        }
    }, [baseUrl]);
    return useCustomElementWithProps(tagName, customProps, htmlProps);
}
export function getAssetsFromWebpack4AssetManifest(baseUrl) {
    return __awaiter(this, void 0, void 0, function* () {
        const getAbsoluteUrl = (path) => new URL(path, baseUrl).toString();
        const response = yield fetch(getAbsoluteUrl("/asset-manifest.json"));
        if (!response.ok) {
            throw new Error("Failed to get manifest");
        }
        const manifest = yield response.json();
        const relativeScriptUrl = manifest.entrypoints.find((x) => x.endsWith(".js"));
        if (!relativeScriptUrl) {
            console.error("Asset manifest did not contain a bundle URL", manifest);
            throw new Error("Failed to find bundle URL in asset manifest");
        }
        const relativeCssUrls = manifest.entrypoints.filter((x) => x.endsWith(".css"));
        return {
            scriptUrl: getAbsoluteUrl(relativeScriptUrl),
            cssUrls: relativeCssUrls.map(getAbsoluteUrl),
        };
    });
}
export function getAssetsFromViteAssetManifest(baseUrl) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        const getAbsoluteUrl = (path) => new URL(path, baseUrl).toString();
        const response = yield fetch(getAbsoluteUrl("/manifest.json").toString());
        if (!response.ok) {
            throw new Error("Failed to get manifest");
        }
        const manifest = yield response.json();
        const relativeScriptUrl = manifest["index.html"]["file"];
        if (!relativeScriptUrl) {
            console.error("Asset manifest did not contain a bundle URL", manifest);
            throw new Error("Failed to find bundle URL in asset manifest");
        }
        const relativeCssUrls = (_a = manifest["index.html"]["css"]) !== null && _a !== void 0 ? _a : [];
        return {
            scriptUrl: getAbsoluteUrl(relativeScriptUrl),
            cssUrls: relativeCssUrls.map(getAbsoluteUrl),
        };
    });
}
/**
 * Dev mode vitejs script. Potentially brittle
 * @param baseUrl
 * @returns
 */
function ensureViteDevPreamble(baseUrl) {
    if ("__vite_plugin_react_preamble_installed__" in window) {
        return;
    }
    const script = document.createElement("script");
    script.type = "module";
    script.textContent = `
    import RefreshRuntime from "${new URL("@react-refresh", baseUrl)}"
    RefreshRuntime.injectIntoGlobalHook(window)
    window.$RefreshReg$ = () => {}
    window.$RefreshSig$ = () => (type) => type
    window.__vite_plugin_react_preamble_installed__ = true
    `;
    document.head.appendChild(script);
}
